import { useCallback, useRef, useState } from "react";

import { ArrowDownIcon, ArrowUpIcon, ResetIcon } from "@/assets/icons";
import { Switch } from "@/components/ui/switch";
import { OTHER_ROW_ID } from "@/constants/dataset";
import { cn } from "@/lib/utils";
import {
  BreakRow,
  BreaksState,
  CustomBreaksValue,
  EColorNames,
  RowMarkerFields
} from "@/types/dataset";
import {
  CUSTOM_BREAKS_MAX_STEPS,
  CUSTOM_BREAKS_MIN_STEPS,
  CUSTOM_BREAKS_OTHER_FILL_COLOR,
  CUSTOM_BREAKS_OTHER_OUTLINE_COLOR,
  getCustomBreakRangeText,
  getDefaultCustomBreaksConfig,
  redistributeCustomBreaks
} from "@/utils/dataset";

import LabelRangePanel, { type SiblingBreak } from "./LabelRangePanel";
import {
  LegendActions,
  LegendColumnHeaders,
  LegendOtherRow,
  LegendRow,
  LegendRowActivePanel
} from "./LegendRow";
import { useLegendSubPanels } from "./useLegendSubPanels";

interface CustomBreaksConfigProps {
  isPOI?: boolean;
  /** Line datasets: single "Stroke" color column (no Fill/outline), drives line-color. */
  strokeOnly?: boolean;
  disabled?: boolean;
  colorSteps?: number;
  colorFamily?: EColorNames;
  min?: number;
  max?: number;
  isInteger?: boolean;
  value?: CustomBreaksValue;
  onPreview?: (value: CustomBreaksValue) => void;
  onSave?: (value: CustomBreaksValue) => void;
  onCancel?: () => void;
  onReset?: () => CustomBreaksValue | null;
}

const mapValueToState = (value: CustomBreaksValue): BreaksState => ({
  rows: value.steps.map((step, index) => ({
    id: `break-${index}`,
    label: step.label,
    start: step.start,
    end: step.end,
    fillColor: step.fillColor,
    outlineColor: step.outlineColor,
    image: step.imageUrl,
    imageName: step.imageName,
    imageAssetId: step.imageAssetId,
    iconId: step.iconId ?? null
  })),
  otherFill: value.otherFillColor,
  otherOutline: value.otherOutlineColor
});

const mapStateToValue = (state: BreaksState): CustomBreaksValue => ({
  steps: state.rows.map((row) => ({
    label: row.label,
    start: row.start,
    end: row.end,
    fillColor: row.fillColor,
    outlineColor: row.outlineColor,
    imageUrl: row.image,
    imageName: row.imageName,
    imageAssetId: row.imageAssetId,
    iconId: row.iconId
  })),
  otherFillColor: state.otherFill,
  otherOutlineColor: state.otherOutline
});

/** Builds break rows for `count` steps from the dataset range (equal interval). */
const buildRows = (
  count: number,
  min: number | undefined,
  max: number | undefined,
  colorFamily: EColorNames,
  isInteger: boolean
): BreakRow[] =>
  redistributeCustomBreaks(
    count,
    min ?? 0,
    max ?? 0,
    colorFamily,
    isInteger
  ).map((step, index) => ({
    id: `break-${index}`,
    label: step.label,
    start: step.start,
    end: step.end,
    fillColor: step.fillColor,
    outlineColor: step.outlineColor,
    image: step.imageUrl,
    imageName: step.imageName,
    imageAssetId: step.imageAssetId,
    iconId: step.iconId ?? null
  }));

const CustomBreaksConfig = ({
  isPOI = false,
  strokeOnly = false,
  disabled = false,
  colorSteps = CUSTOM_BREAKS_MAX_STEPS,
  colorFamily = EColorNames.TEAL,
  min,
  max,
  isInteger = false,
  value,
  onPreview,
  onSave,
  onCancel,
  onReset
}: CustomBreaksConfigProps) => {
  const [useImage, setUseImage] = useState<boolean>(() =>
    Boolean(value?.steps?.some((step) => step.imageUrl || step.iconId))
  );
  const [showRule, setShowRule] = useState(true);
  const lastMarkersRef = useRef<Map<string, RowMarkerFields> | null>(null);
  const initialState = (): BreaksState =>
    value
      ? mapValueToState(value)
      : {
          rows: buildRows(colorSteps, min, max, colorFamily, isInteger),
          otherFill: CUSTOM_BREAKS_OTHER_FILL_COLOR,
          otherOutline: CUSTOM_BREAKS_OTHER_OUTLINE_COLOR
        };

  const [draftState, setDraftState] = useState<BreaksState>(initialState);
  const [base, setBase] = useState<BreaksState>(initialState);
  const [prevValue, setPrevValue] = useState(value);
  if (value !== prevValue) {
    setPrevValue(value);
    if (value) {
      const external = mapValueToState(value);
      setDraftState(external);
      setBase(external);
    }
  }

  const isDirty =
    JSON.stringify(mapStateToValue(draftState)) !==
    JSON.stringify(mapStateToValue(base));

  const {
    activeColor,
    setActiveColor,
    editingRowId,
    setEditingRowId,
    openColor,
    openEdit,
    isActiveSwatch,
    activeRow,
    activeColorValue
  } = useLegendSubPanels({
    disabled,
    rows: draftState.rows,
    otherFill: draftState.otherFill,
    otherOutline: draftState.otherOutline
  });

  const setDraft = useCallback(
    (next: BreaksState) => {
      setDraftState(next);
      onPreview?.(mapStateToValue(next));
    },
    [onPreview]
  );

  const imageMode = useImage && isPOI;
  const hasRange = min !== undefined && max !== undefined;

  const updateRow = (id: string, patch: Partial<BreakRow>) =>
    setDraft({
      ...draftState,
      rows: draftState.rows.map((r) => (r.id === id ? { ...r, ...patch } : r))
    });

  const redistribute = (count: number) =>
    setDraft({
      ...draftState,
      rows: buildRows(count, min, max, colorFamily, isInteger)
    });

  const handleAddStep = () => {
    if (!hasRange || draftState.rows.length >= CUSTOM_BREAKS_MAX_STEPS) return;
    redistribute(draftState.rows.length + 1);
  };

  const handleRemoveStep = () => {
    if (!hasRange || draftState.rows.length <= CUSTOM_BREAKS_MIN_STEPS) return;
    // Reset the active color after removing a step since the breaks are recalculated.
    setActiveColor(null);
    setEditingRowId(null);
    redistribute(draftState.rows.length - 1);
  };

  // Resetting config to default
  const handleReset = () => {
    const resetConfig = onReset?.();
    if (!resetConfig && hasRange) {
      const next = mapValueToState(
        getDefaultCustomBreaksConfig(
          colorFamily,
          colorSteps,
          min,
          max,
          isInteger
        )
      );
      setDraftState(next);
      setBase(next);
      onSave?.(mapStateToValue(next));
    }
    setActiveColor(null);
    setEditingRowId(null);
    setUseImage(false);
    lastMarkersRef.current = null;
  };

  //  Change to image or icon mode
  const handleToggleImage = (checked: boolean) => {
    setUseImage(checked);
    setEditingRowId(null);
    if (checked) {
      // Enabling Image while a break row's color picker is open swaps it for that
      // row's Marker Visual panel (matches Simple). The "Other" row has no
      // per-row image, so its picker is left as-is.
      setActiveColor((prev) =>
        prev && prev.rowId !== OTHER_ROW_ID
          ? { ...prev, target: "image" }
          : prev
      );
      const lastMarkers = lastMarkersRef.current;
      if (!lastMarkers) return;
      setDraft({
        ...draftState,
        rows: draftState.rows.map((r) => {
          const marker = lastMarkers.get(r.id);
          return marker ? { ...r, ...marker } : r;
        })
      });
    } else {
      const markers = new Map<string, RowMarkerFields>(
        draftState.rows
          .filter((r) => r.image || r.iconId)
          .map((r) => [
            r.id,
            {
              image: r.image,
              imageName: r.imageName,
              imageAssetId: r.imageAssetId,
              iconId: r.iconId
            }
          ])
      );
      if (markers.size) lastMarkersRef.current = markers;
      setActiveColor(null);
      setDraft({
        ...draftState,
        rows: draftState.rows.map((r) => ({
          ...r,
          image: null,
          imageName: null,
          imageAssetId: null,
          iconId: null
        }))
      });
    }
  };

  // Saving config
  const handleSave = () => {
    setBase(draftState);
    onSave?.(mapStateToValue(draftState));
    setActiveColor(null);
    setEditingRowId(null);
  };

  // Canceling edits config
  const handleCancel = () => {
    setDraftState(base);
    onCancel?.();
    setActiveColor(null);
    setEditingRowId(null);
  };

  const editingRow = draftState.rows.find((r) => r.id === editingRowId) ?? null;
  const editingIndex = draftState.rows.findIndex((r) => r.id === editingRowId);
  const siblings: SiblingBreak[] = editingRow
    ? draftState.rows
        .map((r, index) => ({ index: index + 1, start: r.start, end: r.end }))
        .filter((_, index) => index !== editingIndex)
    : [];

  const prevRow =
    editingIndex > 0 ? draftState.rows[editingIndex - 1] : undefined;
  const nextRow =
    editingIndex >= 0 ? draftState.rows[editingIndex + 1] : undefined;
  const inputMin = prevRow ? (prevRow.end ?? min) : min;
  const inputMax = nextRow ? (nextRow.start ?? max) : max;

  const handleActiveColorChange = (hex: string) => {
    if (!activeColor?.rowId) return;
    if (activeColor.rowId === OTHER_ROW_ID) {
      setDraft({
        ...draftState,
        ...(activeColor.target === "outline"
          ? { otherOutline: hex }
          : { otherFill: hex })
      });
      return;
    }
    updateRow(
      activeColor.rowId,
      activeColor.target === "outline"
        ? { outlineColor: hex }
        : { fillColor: hex }
    );
  };

  const hasGap = draftState.rows.some((row, index) => {
    const next = draftState.rows[index + 1];
    return (
      next && row.end !== null && next.start !== null && row.end < next.start
    );
  });

  return (
    <div className="flex flex-col gap-2 rounded-xl border border-[#e5e7eb] p-3">
      {/* Image toggle + Reset */}
      <div className="flex items-center justify-between">
        {isPOI ? (
          <button
            type="button"
            className="contents"
            // Don't let the toggle's press dismiss the color picker, so Image
            // swaps the open picker to a marker instead of closing it.
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            <label className="flex items-center gap-2 text-xs text-txtHeading">
              <Switch
                checked={useImage}
                onCheckedChange={handleToggleImage}
                disabled={disabled}
                data-testid="breaks-image-toggle"
              />
              Image
            </label>
          </button>
        ) : (
          <span />
        )}
        <button
          type="button"
          onClick={handleReset}
          disabled={disabled}
          className="text-xs text-primary-teal disabled:opacity-50"
          data-testid="breaks-reset"
        >
          <div className="flex flex-row items-center">
            <ResetIcon className="mr-1 inline h-3 w-3" />
            Reset
          </div>
        </button>
      </div>

      <LegendColumnHeaders
        imageMode={imageMode}
        strokeOnly={strokeOnly}
        withActions
      />

      {/* Break rows */}
      <div className="flex flex-col gap-2">
        {draftState.rows.map((row, index) => {
          const isFirst = index === 0;
          const isLast = index === draftState.rows.length - 1;
          const isFixed = isFirst || isLast;
          return (
            <LegendRow
              key={row.id}
              imageMode={imageMode}
              strokeOnly={strokeOnly}
              image={row.image}
              iconId={row.iconId}
              fillColor={row.fillColor}
              outlineColor={row.outlineColor}
              isFillActive={isActiveSwatch(row.id, ["fill", "image"])}
              isOutlineActive={isActiveSwatch(row.id, ["outline"])}
              disabled={disabled}
              onFillClick={() =>
                openColor(row.id, imageMode ? "image" : "fill")
              }
              onOutlineClick={() => openColor(row.id, "outline")}
              fillTestId={`breaks-fill-${row.id}`}
              outlineTestId={`breaks-outline-${row.id}`}
              onEdit={() => openEdit(row.id)}
              editAriaLabel="Edit break"
              editTestId={`breaks-edit-${row.id}`}
              onDelete={handleRemoveStep}
              deleteAriaLabel="Delete break"
              deleteTestId={`breaks-delete-${row.id}`}
              // "Less" and "More" rows cannot be deleted (PRD).
              deleteDisabled={
                isFixed || draftState.rows.length <= CUSTOM_BREAKS_MIN_STEPS
              }
            >
              {row.label.trim() ? (
                <>
                  {/* Labeled break: name on top, the range value beneath it. */}
                  <span className="truncate text-xs font-medium text-txtHeading">
                    {row.label.trim()}
                  </span>
                  <span
                    className="truncate text-[10px] text-grey"
                    data-testid={`breaks-range-${row.id}`}
                  >
                    {getCustomBreakRangeText(row)}
                  </span>
                </>
              ) : (
                <span
                  className="truncate text-xs font-medium text-txtHeading"
                  data-testid={`breaks-range-${row.id}`}
                >
                  {getCustomBreakRangeText(row)}
                </span>
              )}
            </LegendRow>
          );
        })}
      </div>

      {/* Add Range */}
      <button
        type="button"
        onClick={handleAddStep}
        disabled={
          disabled ||
          !hasRange ||
          draftState.rows.length >= CUSTOM_BREAKS_MAX_STEPS
        }
        className="flex items-center justify-center gap-1 py-1 text-xs font-medium text-primary-teal disabled:opacity-50"
        data-testid="breaks-add-range"
      >
        + Add Range
      </button>

      {/* Show Range Rule */}
      <div className="rounded-lg border border-gainsboro p-2">
        <button
          type="button"
          onClick={() => setShowRule((prev) => !prev)}
          className="flex w-full items-center justify-between text-[10px] font-medium uppercase tracking-wide text-grey"
          data-testid="breaks-rule-toggle"
        >
          <span>Show Range Rule</span>
          <span>
            {showRule ? (
              <ArrowUpIcon className="w-3.5" />
            ) : (
              <ArrowDownIcon className="w-3.5" />
            )}
          </span>
        </button>
        {showRule && (
          <ul className="mt-1 list-disc pl-4 text-[10px] text-grey">
            <li>
              <b>Breaks:</b> Adding or removing a break redistributes all values
              from MIN to MAX.
            </li>
            <li>
              <b>Logic:</b> Ranges exclude the start value but include the end
              value (e.g., 50–75 means &gt; 50 and ≤ 75).
            </li>
          </ul>
        )}
      </div>

      <div className="h-px bg-gainsboro" />

      {/* Other row (system-managed) */}
      <LegendOtherRow
        testIdPrefix="breaks"
        strokeOnly={strokeOnly}
        className={cn(!hasGap && "opacity-50")}
        fillColor={draftState.otherFill}
        outlineColor={draftState.otherOutline}
        isFillActive={isActiveSwatch(OTHER_ROW_ID, ["fill"])}
        isOutlineActive={isActiveSwatch(OTHER_ROW_ID, ["outline"])}
        disabled={disabled || !hasGap}
        onFillClick={() => openColor(OTHER_ROW_ID, "fill")}
        onOutlineClick={() => openColor(OTHER_ROW_ID, "outline")}
      />

      {/* Cancel / Save */}
      <LegendActions
        disabled={disabled || !isDirty}
        onCancel={handleCancel}
        onSave={handleSave}
      />

      <LegendRowActivePanel
        activeColor={activeColor}
        activeRow={activeRow}
        activeColorValue={activeColorValue}
        updateRow={updateRow}
        onColorChange={handleActiveColorChange}
        onClose={() => setActiveColor(null)}
      />

      {editingRow && (
        <LabelRangePanel
          key={editingRow.id}
          label={editingRow.label}
          start={editingRow.start}
          end={editingRow.end}
          isFirst={editingIndex === 0}
          isLast={editingIndex === draftState.rows.length - 1}
          min={min}
          max={max}
          inputMin={inputMin}
          inputMax={inputMax}
          isInteger={isInteger}
          siblings={siblings}
          onSave={(label, start, end) =>
            updateRow(editingRow.id, { label, start, end })
          }
          onClose={() => setEditingRowId(null)}
        />
      )}
    </div>
  );
};

export default CustomBreaksConfig;
